// Redux
import { AsyncResult, Action, ApiAction } from '../../index';
import api from '../../redux-api';

// Utils
import { uniqueArray } from '../../../utils/objects';

// Types
import { ComparisonBenchmarkStoreState } from '../types/store-comparison-benchmark';
import { getMonthEndDataOption } from '../../../utils/comparison';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Stock from '@/types/stock';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import BenchmarkHolding from '@/types/benchmark-holding';
import { batch } from 'react-redux';

// Initial State
const INITIAL_STATE: ComparisonBenchmarkStoreState = {
  comparisonBenchmarks: {},
  comparisonBenchmarksPMED: {},
  comparisonBenchmarksRMED: {},
  comparisonBenchmarksPQED: {},
  comparisonBenchmarksRQED: {},
};

// Actions
const FETCH_COMPARISON_BENCHMARK_BY_ID: ApiAction = {
  type: 'connect/comparison-benchmarks/store/fetch',
  endpoint: 'comparison-benchmarks/:id/',
};

const FETCH_MANY_COMPARISON_BENCHMARKS: ApiAction = {
  type: 'connect/comparison-benchmarks/store/fetch-many',
  endpoint: 'comparison-benchmarks/',
};

const COMPARISON_BENCHMARKS_STORE_GET: Action = { type: 'connect/comparison-benchmarks/store/get' };
const COMPARISON_BENCHMARKS_STORE_GET_MANY: Action = { type: 'connect/comparison-benchmarks/store/get-many' };
const COMPARISON_BENCHMARKS_STORE_ADD: Action = { type: 'connect/comparison-benchmarks/store/add' };

// Reducer
export default function reducer(state: ComparisonBenchmarkStoreState = INITIAL_STATE, action: Action): ComparisonBenchmarkStoreState {
  switch (action.type) {
    case COMPARISON_BENCHMARKS_STORE_ADD.type:
      switch (action.monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          return {
            ...state,
            comparisonBenchmarksRMED: { ...state.comparisonBenchmarksRMED, [action.benchmark._id]: action.benchmark },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          return {
            ...state,
            comparisonBenchmarksPMED: { ...state.comparisonBenchmarksPMED, [action.benchmark._id]: action.benchmark },
          };
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          return {
            ...state,
            comparisonBenchmarksRQED: { ...state.comparisonBenchmarksRQED, [action.benchmark._id]: action.benchmark },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          return {
            ...state,
            comparisonBenchmarksPQED: { ...state.comparisonBenchmarksPQED, [action.benchmark._id]: action.benchmark },
          };
        default:
          return {
            ...state,
            comparisonBenchmarks: { ...state.comparisonBenchmarks, [action.benchmark._id]: action.benchmark },
          };
      }
    default:
      return state;
  }
}

// Action Creators

/**
 * Retrieve Benchmark data either from memory, local cache or directly from the API
 * @param id Benchmark's unique identifier
 */
export const getComparisonBenchmark =
  (id: string): AsyncResult<ComparisonBenchmark> =>
  (dispatch, getState) => {
    dispatch({ ...COMPARISON_BENCHMARKS_STORE_GET, id });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    // See if we got it in State
    let stateBenchmark;
    switch (monthEndDataOption) {
      case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
        stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksRMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
        stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksPMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
        stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksRQED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
        stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksPQED[id];
        break;
      default:
        stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarks[id];
        break;
    }
    if (stateBenchmark) {
      return Promise.resolve(stateBenchmark);
    }

    return api.get(FETCH_COMPARISON_BENCHMARK_BY_ID, { id, ...(monthEndDataOption && { monthEndDataOption }) }).then((res: { data: any }) => {
      const benchmark = res.data as ComparisonBenchmark;
      dispatch({ ...COMPARISON_BENCHMARKS_STORE_ADD, benchmark, monthEndDataOption });
      return benchmark;
    });
  };

/**
 * Retrieve Benchmark data (including its corresponding riskFree Benchmark if available) either from memory, local cache or directly from the API
 * @param id Benchmark's unique identifier
 */
export const getComparisonBenchmarkData =
  (id: string): AsyncResult<{ benchmark: ComparisonBenchmark; riskFree?: ComparisonBenchmark }> =>
  async dispatch => {
    const benchmark = await dispatch(getComparisonBenchmark(id));
    let riskFree;
    if (benchmark.riskFreeId) {
      riskFree = await dispatch(getComparisonBenchmark(benchmark.riskFreeId));
    }
    return { benchmark, riskFree };
  };

/**
 * Retrieve multiple Benchmarks data either from memory, local cache or directly from the API
 * @param ids Benchmark's unique identifiers
 */
export const getManyComparisonBenchmarkData =
  (rawIds: string[]): AsyncResult<ComparisonBenchmark[]> =>
  (dispatch, getState) => {
    const ids: string[] = rawIds.filter(id => id != null && id !== '');
    dispatch({ ...COMPARISON_BENCHMARKS_STORE_GET_MANY, ids });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const remaining = new Set(ids);
    const benchmarkData = [] as ComparisonBenchmark[];

    for (const id of ids) {
      // See if we got it in State
      let stateBenchmark;
      switch (monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksRMED[id];
          break;
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksPMED[id];
          break;
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksRQED[id];
          break;
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarksPQED[id];
          break;
        default:
          stateBenchmark = getState().comparisonBenchmarks.store.comparisonBenchmarks[id];
          break;
      }
      if (stateBenchmark) {
        benchmarkData.push(stateBenchmark);
        remaining.delete(id);
        continue;
      }
    }

    // Fetch the rest from the API if we're missing any
    if (remaining.size) {
      return api
        .get(FETCH_MANY_COMPARISON_BENCHMARKS, { ids: Array.from(remaining.values()).join('-'), ...(monthEndDataOption && { monthEndDataOption }) })
        .then((res: { data: any }) => {
          batch(() => {
            for (const benchmark of res.data as ComparisonBenchmark[]) {
              dispatch({ ...COMPARISON_BENCHMARKS_STORE_ADD, benchmark, monthEndDataOption });
            }
          });

          return benchmarkData.concat(res.data);
        })
        .catch(() => benchmarkData);
    }

    // Return the cached benchmarks if no API call is required
    return Promise.resolve(benchmarkData);
  };

/**
 * Retrieve all benchmarks data associated with the requested fund,
 * either from local cache, memory or directly from the API.
 *
 * @param stock Stock for which the benchmark data should be retrieved.
 * @returns Array of benchmarks.
 */

export const getStockBenchmarks =
  (stock: Stock): AsyncResult<ComparisonBenchmark[]> =>
  dispatch => {
    const ids = [stock.benchmarkId, stock.riskFreeId];
    return dispatch(getManyComparisonBenchmarkData(ids));
  };

/**
 * Retrieve all benchmarks data associated with the requested funds,
 * either from local cache, memory or directly from the API.
 *
 * @param funds Funds for which the benchmark data should be retrieved.
 * @returns Array of benchmarks.
 */

export const getStocksBenchmarks =
  (stocks: Stock[]): AsyncResult<ComparisonBenchmark[]> =>
  dispatch => {
    const benchmarkIds = stocks.reduce((ids: string[], stock) => [...ids, stock.benchmarkId, stock.riskFreeId], []);

    const uniqueBenchmarkIds = uniqueArray(benchmarkIds);
    return dispatch(getManyComparisonBenchmarkData(uniqueBenchmarkIds));
  };

export const getRiskFreeBenchmarks =
  (benchmarks: BenchmarkHolding[]): AsyncResult<ComparisonBenchmark[]> =>
  dispatch => {
    const benchmarkIds = benchmarks.reduce((ids: (string | undefined)[], benchmark) => [...ids, benchmark.riskFreeId], []);

    const uniqueBenchmarkIds = uniqueArray(benchmarkIds.filter(id => id != null) as string[]);

    if (uniqueBenchmarkIds.length === 0) {
      return Promise.resolve([]);
    }
    return dispatch(getManyComparisonBenchmarkData(uniqueBenchmarkIds));
  };
